import React, { useEffect, useRef, useState } from 'react'
import { useQuery } from 'react-query'
import { Space, Button, Card, Select, Typography } from 'antd'
import get from 'lodash.get'
import set from 'lodash.set'
import FormContent from '../FormContent'
import { PRIORITY_MAP, scrollToTop } from '../../../utils/utils'
import { formOptions } from '../../../forms/blocks'
import { getFromKey, getFixedDesignCategory } from '../utils/orderHelpers'
import { getProductSizes } from '../../../network/ProductSize'

const { Text } = Typography

const Step3 = ({
  values,
  errors,
  handleChange,
  handleBack,
  handleNext,
  afterStep3,
  setAfterStep3,
  innerStep,
  setInnerStep,
  validateForm,
  clientOrders,
  goToPayment,
  canDirectPayment,
  setCanDirectPayment,
}) => {
  const [isValidated, setIsValidated] = useState(false)
  const measureValues = values.orderProducts
    .map((prod, index) => {
      const priority = PRIORITY_MAP[getFromKey(prod)]
      return {
        product: prod,
        index,
        key: getFromKey(prod),
        priority,
      }
    })
    .filter((item) => {
      // Include custom, collection, and fixed_design products that need measurements
      return (
        item.product.orderProductType === 'custom' ||
        item.product.orderProductType === 'collection' ||
        item.product.orderProductType === 'fixed_design'
      )
    })
    .sort((a, b) => (b.priority || 0) - (a.priority || 0))

  const maxStep = measureValues.length - 1
  const [measureStep, setMeasureStep] = useState(afterStep3 ? maxStep : 0)
  const [selectedSizeId, setSelectedSizeId] = useState(null)
  const [selectedPresetId, setSelectedPresetId] = useState(null)
  const [selectedSizeIndex, setSelectedSizeIndex] = useState(null)
  const measurementsSnapshotRef = useRef({})
  // Stores the base measurements coming from a selected size preset, per product index
  const sizePresetBaseRef = useRef({})

  // Collect unique product types from custom products (only custom products can use size presets)
  const customProductTypes = Array.from(
    new Set(
      measureValues
        .filter((item) => item.product.orderProductType === 'custom')
        .map((item) => item.key)
        .filter(Boolean)
    )
  )

  // Fetch product sizes filtered by product types in the order
  const productSizesQuery = useQuery(
    [
      'getProductSizes',
      {
        sort: [],
        range: [0, 9999],
        filter:
          customProductTypes.length > 0
            ? { productType: { $in: customProductTypes } }
            : { productType: '__none__' }, // Fetch nothing if no custom products
        tabFilter: {},
      },
    ],
    getProductSizes,
    {
      refetchOnWindowFocus: false,
      enabled: customProductTypes.length > 0, // Only fetch if we have custom products
    }
  )
  const productSizesList = get(
    productSizesQuery.data,
    'list',
    Array.isArray(productSizesQuery.data) ? productSizesQuery.data : []
  )

  useEffect(() => {
    validateForm()
  }, [measureStep])

  useEffect(() => {
    if (!measureValues.length) {
      if (afterStep3) {
        handleBack()
        setAfterStep3(false)
      } else {
        handleNext()
        setAfterStep3(true)
      }
    } else if (afterStep3) {
      setInnerStep(measureValues[maxStep].index)
    } else {
      setInnerStep(measureValues[0].index)
    }
  }, [])

  // Snapshot measurements when switching to a product (for Reset)
  useEffect(() => {
    const productIndex = measureValues[measureStep]?.index
    setSelectedSizeId(null)
    setSelectedPresetId(null)
    setSelectedSizeIndex(null)
    if (productIndex == null) return
    const current = get(values, `orderProducts.${productIndex}.skuMeasurements.0`, {})
    measurementsSnapshotRef.current[productIndex] = JSON.parse(JSON.stringify(current))
  }, [measureStep])

  const scheduleScrollToTop = () => {
    requestAnimationFrame(() => {
      scrollToTop()
      setTimeout(scrollToTop, 100)
    })
  }

  const handleMeasureNext = () => {
    if (measureStep >= maxStep) {
      setCanDirectPayment?.(true)
      handleNext()
      setAfterStep3(true)
    } else {
      setInnerStep(measureValues[measureStep + 1].index)
      setMeasureStep(measureStep + 1)
    }
    scheduleScrollToTop()
  }

  const handleMeasureBack = () => {
    if (measureStep <= 0) {
      handleBack()
      setAfterStep3(false)
    } else {
      setInnerStep(measureValues[measureStep - 1].index)
      setMeasureStep(measureStep - 1)
    }
    scheduleScrollToTop()
  }

  const stepKey = get(
    measureValues,
    `${measureStep}.product.orderProductType`,
    false
  )

  const checkValidity = (toPaymentOrStepId) => {
    validateForm().then((res) => {
      if (Object.keys(res).length) {
        setIsValidated(true)
      } else {
        if (toPaymentOrStepId === true) {
          goToPayment?.()
        } else if (typeof toPaymentOrStepId === 'number') {
          setMeasureStep(toPaymentOrStepId)
          scheduleScrollToTop()
        } else {
          handleMeasureNext()
        }
        setIsValidated(false)
      }
    })
  }

  const productIndex = measureValues[measureStep]?.index
  const isCustomProduct = measureValues[measureStep]?.product?.orderProductType === 'custom'
  // For custom products, actual type is measureValues[measureStep].key (e.g. 'skirt', 'vest')
  // stepKey is orderProductType ('custom'), API productType is 'skirt'/'vest'
  const currentProductType = measureValues[measureStep]?.key

  // Fixed design ID for the current product (only valid when stepKey === 'fixed_design')
  const currentFixedDesignId =
    stepKey === 'fixed_design'
      ? get(measureValues[measureStep]?.product, 'orderFixedDesign.productId') || null
      : null

  // Fetch size presets linked to the current fixed design
  const fixedDesignSizesQuery = useQuery(
    [
      'getProductSizesForFixedDesign',
      {
        sort: [],
        range: [0, 99],
        filter: currentFixedDesignId
          ? { fixedDesignId: currentFixedDesignId }
          : { productType: '__none__' },
        tabFilter: {},
      },
    ],
    getProductSizes,
    {
      refetchOnWindowFocus: false,
      enabled: !!currentFixedDesignId,
    }
  )
  const fixedDesignSizesList = get(
    fixedDesignSizesQuery.data,
    'list',
    Array.isArray(fixedDesignSizesQuery.data) ? fixedDesignSizesQuery.data : []
  )

  // Presets for current product type (new model: each preset has sizes[] with name + measurements)
  const presetsForCurrentType = (productSizesList || []).filter(
    (p) => p.productType === currentProductType
  )
  const measureFormKey = formOptions[currentProductType]?.measuresForms?.[0]?.key
  const measureSubKey = measureFormKey ? measureFormKey.split('.').pop() : null

  const applyProductSize = (presetId, sizeIndex) => {
    if (productIndex == null || !measureSubKey || !presetId || sizeIndex == null) return
    const preset = presetsForCurrentType.find((p) => (p.id || p._id) === presetId)
    const sizeEntry = preset?.sizes?.[sizeIndex]
    const measurements = sizeEntry?.measurements
    if (!measurements) return

    // Store the base measurements from the selected size preset for comparison display
    if (!sizePresetBaseRef.current[productIndex]) {
      sizePresetBaseRef.current[productIndex] = {}
    }
    set(
      sizePresetBaseRef.current[productIndex],
      `skuMeasurements.0.${measureSubKey}`,
      { ...measurements }
    )

    handleChange({
      target: {
        name: `orderProducts.${productIndex}.skuMeasurements.0.${measureSubKey}`,
        value: { ...measurements },
      },
    })

    // Persist preset info/meta inside the measurement block so it goes to the API payload
    handleChange({
      target: {
        name: `orderProducts.${productIndex}.skuMeasurements.0.${measureSubKey}.__sizePresetMeta`,
        value: {
          presetId,
          sizeIndex,
          presetName:
            get(preset, 'name.en') ||
            get(preset, 'name.zh') ||
            null,
          sizeName:
            get(sizeEntry, 'name.en') ||
            get(sizeEntry, 'name.zh') ||
            null,
          base: { ...measurements },
        },
      },
    })
    setSelectedSizeId(`${presetId}-${sizeIndex}`)
  }

  // Apply a size preset from a fixed-design-linked ProductSize
  const applyFixedDesignSize = (presetId, sizeIndex) => {
    if (productIndex == null || !presetId || sizeIndex == null) return
    const preset = fixedDesignSizesList.find((p) => (p.id || p._id) === presetId)
    const sizeEntry = preset?.sizes?.[sizeIndex]
    const measurements = sizeEntry?.measurements
    if (!measurements || !preset?.productType) return

    const fmKey = formOptions[preset.productType]?.measuresForms?.[0]?.key
    const subKey = fmKey ? fmKey.split('.').pop() : null
    if (!subKey) return

    if (!sizePresetBaseRef.current[productIndex]) {
      sizePresetBaseRef.current[productIndex] = {}
    }
    set(
      sizePresetBaseRef.current[productIndex],
      `skuMeasurements.0.${subKey}`,
      { ...measurements }
    )

    handleChange({
      target: {
        name: `orderProducts.${productIndex}.skuMeasurements.0.${subKey}`,
        value: { ...measurements },
      },
    })
    handleChange({
      target: {
        name: `orderProducts.${productIndex}.skuMeasurements.0.${subKey}.__sizePresetMeta`,
        value: {
          presetId,
          sizeIndex,
          presetName: get(preset, 'name.en') || get(preset, 'name.zh') || null,
          sizeName: get(sizeEntry, 'name.en') || get(sizeEntry, 'name.zh') || null,
          base: { ...measurements },
        },
      },
    })
    setSelectedSizeId(`fd-${presetId}-${sizeIndex}`)
  }

  const resetMeasurements = () => {
    const snap = measurementsSnapshotRef.current[productIndex]
    if (productIndex == null || !snap) return
    ;(formOptions[currentProductType]?.measuresForms || []).forEach((f) => {
      const subKey = f.key?.split('.').pop()
      if (subKey && snap[subKey] != null) {
        handleChange({
          target: {
            name: `orderProducts.${productIndex}.skuMeasurements.0.${subKey}`,
            value: { ...snap[subKey] },
          },
        })
      }
    })
    setSelectedSizeId(null)
    setSelectedPresetId(null)
    setSelectedSizeIndex(null)
    if (sizePresetBaseRef.current[productIndex]) {
      delete sizePresetBaseRef.current[productIndex]
    }
  }

  const handleApplyPreviousSize = () => {
    const cpyMeasure = JSON.parse(JSON.stringify([...measureValues]))
    let prevMeasureValues = JSON.parse(JSON.stringify(cpyMeasure))
      .reverse()
      .toSpliced(0, cpyMeasure?.length - measureStep)
    const currentMeasureProductType = get(cpyMeasure, `${measureStep}.key`, '')
    // For fixed_design, formOptions has no 'fixed_design.measuresForms' – resolve to product category (e.g. cashmere, shirt)
    const resolvedMeasureProductType = currentMeasureProductType === 'fixed_design'
      ? (getFixedDesignCategory(get(cpyMeasure, `${measureStep}.product`)) || currentMeasureProductType)
      : currentMeasureProductType

    let keys = []
    if (currentMeasureProductType === 'collection') {
      keys = Object.keys(
        get(cpyMeasure, `${measureStep}.product.skuMeasurements.0`, {})
      ).filter((e) => {
        if (
          (e === 'skuMeasurementShoes' &&
            !cpyMeasure[measureStep].product.skuMeasurements[0]
              ?.skuMeasurementShoes?.footMeasure) ||
          (e === 'skuMeasurementSkirt' &&
            !cpyMeasure[measureStep].product.skuMeasurements[0]
              ?.skuMeasurementSkirt?.waist) ||
          e === '_id'
        ) {
          return false
        } else {
          return true
        }
      })
    } else {
      const forms = get(
        formOptions,
        `${resolvedMeasureProductType}.measuresForms`,
        []
      )
      keys = Array.isArray(forms) ? forms.map((e) => {
        const key = e.key.split('.')

        return key[key.length - 1]
      }) : []
    }

    const productValues = []
    for (const key of keys) {
      productValues.push({
        pathKey: key,
        value: null,
        order: null,
      })
    }

    let filled = 0
    for (const prevMeasure of prevMeasureValues) {
      if (filled === keys.length) break

      const objectKeys = Object.keys(
        values.orderProducts[prevMeasure.index].skuMeasurements[0]
      )

      for (const key of objectKeys) {
        const idx = productValues.findIndex((prod) => prod.pathKey === key)

        if (
          idx !== -1 &&
          !JSON.parse(JSON.stringify(productValues[idx].value))
        ) {
          set(
            productValues,
            `${idx}.value`,
            prevMeasure.product.skuMeasurements[0][key]
          )
          filled++
        }
      }
    }

    if (filled < keys.length) {
      for (const order of clientOrders.data) {
        if (filled === keys.length) break

        const reverseOrder = [...(order.orderProducts || [])].reverse()

        for (const prod of reverseOrder) {
          const lastMeasureIdx = prod.skuMeasurements?.length - 1

          if (!!prod.skuMeasurements?.length) {
            const currKeys = Object.keys(
              prod.skuMeasurements[lastMeasureIdx]
            ).filter((e) => {
              if (
                (e === 'skuMeasurementShoes' &&
                  !prod.skuMeasurements[lastMeasureIdx]?.skuMeasurementShoes
                    ?.footMeasure) ||
                (e === 'skuMeasurementSkirt' &&
                  !prod.skuMeasurements[lastMeasureIdx]?.skuMeasurementSkirt
                    ?.waist) ||
                e === '_id'
              ) {
                return false
              } else {
                return true
              }
            })

            for (const key of currKeys) {
              const idx = productValues.findIndex(
                (prod) => prod.pathKey === key
              )

              if (
                idx !== -1 &&
                !productValues[idx].value &&
                !!prod.skuMeasurements[lastMeasureIdx][key]
              ) {
                set(
                  productValues,
                  `${idx}.value`,
                  prod.skuMeasurements[lastMeasureIdx][key]
                )
                set(productValues, `${idx}.order`, order.orderID)
                filled++
              }
            }
          }
        }
      }
    }

    for (const info of productValues) {
      if (!!info.value) {
        handleChange({
          target: {
            name: `orderProducts.${innerStep}.skuMeasurements.0.${info.pathKey}`,
            value: info.value,
          },
        })
      }
    }
  }

  return (
    <Space direction="vertical" style={{ width: '100%' }}>
      <div
        style={{
          display: 'flex',
          justifyContent: 'end',
          flexDirection: 'row',
          position: 'relative',
        }}
      >
        <div
          style={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            display: 'flex',
            flexDirection: 'column',
            textAlign: 'center',
          }}
        >
          {measureValues[measureStep] && (
            <>
              <Text strong>
                {measureValues[measureStep].product.orderProductType === 'collection'
                  ? get(measureValues[measureStep].product, 'orderCollection.productName', 'Collection Product')
                  : measureValues[measureStep].product.orderProductType === 'fixed_design'
                  ? get(measureValues[measureStep].product, 'orderFixedDesign.productName.en', '') ||
                    get(measureValues[measureStep].product, 'orderFixedDesign.productName.zh', '') ||
                    'Fixed Design Product'
                  : formOptions[measureValues[measureStep].key]?.name || 'Product'}
              </Text>
              <div
                style={{
                  color: '#1890ff',
                }}
              >
                {measureValues.map((e, idx) =>
                  idx === measureStep ? (
                    <span key={`${e.key}-${idx}`}>&#9679;</span>
                  ) : (
                    <span
                      key={`${e.key}-${idx}`} 
                      style={{ cursor: 'pointer' }}
                      onClick={() => checkValidity(idx)}
                    >
                      &#9675;
                    </span>
                  )
                )}
              </div>
            </>
          )}
        </div>
        <Button
          loading={clientOrders.isLoading}
          onClick={handleApplyPreviousSize}
          type="primary"
        >
          Use previous size
        </Button>
      </div>
      {measureValues.length > 0 &&
        (stepKey === 'collection' ? (
          get(
            measureValues,
            `${measureStep}.product.orderCollection.productType`,
            []
          ).map((e) =>
            formOptions[e]?.measuresForms ? (
              <FormContent
                key={`orderProducts.${measureValues[measureStep]?.index}-${e}`}
                values={values}
                errors={errors}
                hideErrors={!isValidated}
                path={`orderProducts.${measureValues[measureStep]?.index}`}
                handleChange={handleChange}
                forms={formOptions[e]?.measuresForms}
              />
            ) : (
              <div
                key={`orderProducts.${measureValues[measureStep]?.index}-${e}`}
                style={{
                  textAlign: 'center',
                  fontWeight: 'bold',
                  fontSize: 21,
                  margin: 16,
                }}
              >
                No measurement.
              </div>
            )
          )
        ) : stepKey === 'fixed_design' ? (
          (() => {
            // Extract product types from details for fixed_design
            const details = get(
              measureValues,
              `${measureStep}.product.orderFixedDesign.details`,
              []
            )
            const productTypes = []
            details.forEach((detail) => {
              Object.keys(detail).forEach((key) => {
                if (key !== '_id' && !productTypes.includes(key)) {
                  productTypes.push(key)
                }
              })
            })

            return (
              <React.Fragment>
                {/* Size preset selector — only shown when presets exist for this fixed design */}
                {fixedDesignSizesList.length > 0 && (
                  <Card size="small" title="Apply a size preset" style={{ marginBottom: 16 }}>
                    <Space wrap>
                      <Select
                        allowClear
                        placeholder="Select a size preset"
                        style={{ minWidth: 200 }}
                        value={selectedPresetId || undefined}
                        loading={fixedDesignSizesQuery.isLoading}
                        onChange={(presetId) => {
                          if (!presetId) {
                            setSelectedPresetId(null)
                            setSelectedSizeIndex(null)
                            setSelectedSizeId(null)
                            return
                          }
                          setSelectedPresetId(presetId)
                          setSelectedSizeIndex(null)
                        }}
                      >
                        {fixedDesignSizesList.map((preset) => (
                          <Select.Option key={preset.id || preset._id} value={preset.id || preset._id}>
                            {get(preset, 'name.en', get(preset, 'name.zh', 'Preset'))}
                          </Select.Option>
                        ))}
                      </Select>

                      <Select
                        allowClear
                        placeholder="Select a size"
                        style={{ minWidth: 200 }}
                        value={
                          selectedSizeIndex === null || selectedSizeIndex === undefined
                            ? undefined
                            : selectedSizeIndex
                        }
                        disabled={!selectedPresetId}
                        onChange={(sizeIdx) => {
                          if (sizeIdx === undefined || sizeIdx === null) {
                            setSelectedSizeIndex(null)
                            setSelectedSizeId(null)
                            return
                          }
                          setSelectedSizeIndex(sizeIdx)
                          applyFixedDesignSize(selectedPresetId, sizeIdx)
                        }}
                      >
                        {(fixedDesignSizesList.find(
                          (p) => (p.id || p._id) === selectedPresetId
                        )?.sizes || []).map((sizeEntry, idx) => (
                          <Select.Option key={idx} value={idx}>
                            {get(sizeEntry, 'name.en', get(sizeEntry, 'name.zh', `Size ${idx + 1}`))}
                          </Select.Option>
                        ))}
                      </Select>

                      <Button onClick={resetMeasurements} type="default">
                        Reset measurements
                      </Button>
                    </Space>
                  </Card>
                )}

                {productTypes.map((e) =>
                  formOptions[e]?.measuresForms ? (
                    <FormContent
                      key={`orderProducts.${measureValues[measureStep]?.index}-${e}`}
                      values={values}
                      errors={errors}
                      hideErrors={!isValidated}
                      path={`orderProducts.${measureValues[measureStep]?.index}`}
                      handleChange={handleChange}
                      forms={formOptions[e]?.measuresForms}
                      comparisonBase={sizePresetBaseRef.current[productIndex]}
                      comparisonPath={`orderProducts.${productIndex}`}
                    />
                  ) : (
                    <div
                      key={`orderProducts.${measureValues[measureStep]?.index}-${e}`}
                      style={{
                        textAlign: 'center',
                        fontWeight: 'bold',
                        fontSize: 21,
                        margin: 16,
                      }}
                    >
                      No measurement.
                    </div>
                  )
                )}
              </React.Fragment>
            )
          })()
        ) : stepKey === 'adjustment' ? (
          <FormContent
            key={`orderProducts.${measureValues[measureStep]?.index}-${stepKey}`}
            values={values}
            errors={errors}
            hideErrors={!isValidated}
            path={`orderProducts.${measureValues[measureStep]?.index}`}
            handleChange={handleChange}
            forms={
              formOptions[
                measureValues[measureStep]?.product?.orderAdjustment?.type
              ]?.measuresForms
            }
          />
        ) : formOptions[measureValues[measureStep]?.key]?.measuresForms ? (
          <>
            {isCustomProduct && (
              <Card size="small" title="Apply a size preset" style={{ marginBottom: 16 }}>
                <Space wrap>
                  <Select
                    allowClear
                    placeholder="Select a size preset"
                    style={{ minWidth: 200 }}
                    value={selectedPresetId || undefined}
                    onChange={(presetId) => {
                      if (!presetId) {
                        setSelectedPresetId(null)
                        setSelectedSizeIndex(null)
                        setSelectedSizeId(null)
                        return
                      }
                      setSelectedPresetId(presetId)
                      setSelectedSizeIndex(null)
                    }}
                  >
                    {presetsForCurrentType.map((preset) => (
                      <Select.Option key={preset.id || preset._id} value={preset.id || preset._id}>
                        {get(preset, 'name.en', get(preset, 'name.zh', 'Preset'))}
                      </Select.Option>
                    ))}
                  </Select>

                  <Select
                    allowClear
                    placeholder="Select a size"
                    style={{ minWidth: 200 }}
                    value={
                      selectedSizeIndex === null || selectedSizeIndex === undefined
                        ? undefined
                        : selectedSizeIndex
                    }
                    disabled={!selectedPresetId}
                    onChange={(sizeIdx) => {
                      if (sizeIdx === undefined || sizeIdx === null) {
                        setSelectedSizeIndex(null)
                        setSelectedSizeId(null)
                        return
                      }
                      setSelectedSizeIndex(sizeIdx)
                      applyProductSize(selectedPresetId, sizeIdx)
                    }}
                  >
                    {(presetsForCurrentType.find(
                      (p) => (p.id || p._id) === selectedPresetId
                    )?.sizes || []
                    ).map((sizeEntry, idx) => (
                      <Select.Option key={idx} value={idx}>
                        {get(
                          sizeEntry,
                          'name.en',
                          get(sizeEntry, 'name.zh', `Size ${idx + 1}`)
                        )}
                      </Select.Option>
                    ))}
                  </Select>

                  <Button onClick={resetMeasurements} type="default">
                    Reset measurements
                  </Button>
                </Space>
              </Card>
            )}
            <FormContent
              key={`orderProducts.${measureValues[measureStep]?.index}-${stepKey}`}
              values={values}
              errors={errors}
              hideErrors={!isValidated}
              path={`orderProducts.${measureValues[measureStep]?.index}`}
              handleChange={handleChange}
              forms={formOptions[measureValues[measureStep]?.key]?.measuresForms}
              comparisonBase={sizePresetBaseRef.current[productIndex]}
              comparisonPath={`orderProducts.${productIndex}`}
            />
          </>
        ) : (
          <div
            key={`orderProducts.${measureValues[measureStep]?.index}-${stepKey}`}
            style={{
              textAlign: 'center',
              fontWeight: 'bold',
              fontSize: 21,
              margin: 16,
            }}
          >
            No measurement.
          </div>
        ))}

      <Space>
        <Button onClick={handleMeasureBack} type="secondary">
          Previous
        </Button>
        <Button
          onClick={() => checkValidity()}
          type="primary"
        >
          Next
        </Button>
        {goToPayment && (
          <Button
            disabled={canDirectPayment === false}
            type="primary"
            onClick={() => checkValidity(true)}
          >
            Payment
          </Button>
        )}
      </Space>
    </Space>
  )
}

export default Step3
